import {useEffect, useRef, useState} from 'react';
import {MessageType} from '../enum';
import {
    arrayBufferToBase64,
    BYTES_PER_SAMPLE,
    STEP_IN_SEC,
    TRANSCRIPTION_RATE,
    ulawFromPCM,
    workletCode
} from "../constants/transcription";
import {USE_ULAW} from "../config";

const log = (message, data = null) => {
    if (process.env.REACT_APP_ENABLE_LOGS !== 'false') {
        console.log(`[AudioRecorder] ${message}`, data || '');
    }
};

const logError = (message, error = null) => {
    console.error(`[AudioRecorder] ${message}`, error || '');
};

export const useAudioRecorder = (localStream, isMuted, callActive, metadata, sendMessage) => {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [audioStats, setAudioStats] = useState({
        totalChunksSent: 0,
        totalBytesSent: 0,
        lastChunkSize: 0,
        averageChunkSize: 0,
        errors: 0,
    });
    const [recordingState, setRecordingState] = useState('idle');

    // AudioWorklet refs
    const audioContextRef = useRef(null);
    const workletNodeRef = useRef(null);
    const analyserRef = useRef(null);

    // PCM buffer for accumulating audio data
    const pcmBufferRef = useRef([]);
    const bufferCountRef = useRef(0);
    const lastSendTimeRef = useRef(Date.now());

    // Sending interval
    const sendIntervalRef = useRef(null);
    const voiceDetectionIntervalRef = useRef(null);

    // Use refs to avoid re-renders
    const isMutedRef = useRef(isMuted);
    const callActiveRef = useRef(callActive);
    const isSpeakingRef = useRef(isSpeaking);
    const metadataRef = useRef(metadata);
    const sendMessageRef = useRef(sendMessage);

    // Update refs when values change
    useEffect(() => {
        isMutedRef.current = isMuted;
    }, [isMuted]);

    useEffect(() => {
        callActiveRef.current = callActive;
    }, [callActive]);

    useEffect(() => {
        isSpeakingRef.current = isSpeaking;
    }, [isSpeaking]);

    useEffect(() => {
        metadataRef.current = metadata;
    }, [metadata]);

    useEffect(() => {
        sendMessageRef.current = sendMessage;
    }, [sendMessage]);

    // Setup audio recording - ONLY when call is active and not muted
    useEffect(() => {
        // Don't set up recording if conditions not met
        if (!localStream || isMuted || !callActive) {
            log('Not setting up recording', {
                hasStream: !!localStream,
                isMuted,
                callActive
            });
            setRecordingState('idle');
            setIsSpeaking(false);
            return;
        }

        let cleanedUp = false;

        const setupAudioWorklet = async () => {
            try {
                log('Setting up AudioWorklet recording...');
                setRecordingState('ready');

                // Create AudioContext with transcription sample rate
                const audioContext = new (window.AudioContext || window.webkitAudioContext)({
                    sampleRate: TRANSCRIPTION_RATE,
                    latencyHint: 'interactive'
                });
                audioContextRef.current = audioContext;

                log(`AudioContext created | Sample rate: ${audioContext.sampleRate}Hz | State: ${audioContext.state}`);

                if (audioContext.state === 'suspended') {
                    log('⚠️  AudioContext suspended, resuming...');
                    await audioContext.resume();
                    log(`✅ AudioContext resumed | State: ${audioContext.state}`);
                }

                // Create blob from worklet code
                const workletBlob = new Blob([workletCode], {type: 'application/javascript'});
                const workletUrl = URL.createObjectURL(workletBlob);

                // Load AudioWorklet module
                await audioContext.audioWorklet.addModule(workletUrl);
                log('✅ AudioWorklet module loaded');

                // Clean up blob URL
                URL.revokeObjectURL(workletUrl);

                // Create media stream source
                const source = audioContext.createMediaStreamSource(localStream);
                log('✅ Media stream source created');

                // Check if tracks are enabled
                const audioTracks = localStream.getAudioTracks();
                log(`📊 Audio tracks: ${audioTracks.length}`, audioTracks.map(t => ({
                    kind: t.kind,
                    enabled: t.enabled,
                    muted: t.muted,
                    readyState: t.readyState
                })));

                // Create AudioWorklet node
                const workletNode = new AudioWorkletNode(audioContext, 'audio-transcription-processor');
                workletNodeRef.current = workletNode;
                log('✅ AudioWorklet node created');

                // Create analyser for voice detection
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 2048;
                analyser.smoothingTimeConstant = 0.8;
                analyserRef.current = analyser;
                log('✅ Analyser created');

                // Connect: source → worklet → analyser → destination
                source.connect(workletNode);
                workletNode.connect(analyser);
                analyser.connect(audioContext.destination);

                log('✅ Audio pipeline connected: source → worklet → analyser → destination');

                // Handle messages from worklet
                workletNode.port.onmessage = (event) => {
                    if (cleanedUp) return;

                    const {type, data} = event.data;

                    if (type === 'ready') {
                        log('✅ AudioWorklet processor ready', event.data.config);
                        setRecordingState('recording');
                    } else if (type === 'audioData') {
                        // Receive PCM data from worklet
                        const pcmData = new Int16Array(data);
                        pcmBufferRef.current.push(pcmData);
                        bufferCountRef.current++;
                    } else if (type === 'reset-complete') {
                        log('✅ AudioWorklet buffer reset');
                    }
                };

                // Send accumulated PCM data every STEP_IN_SEC seconds
                sendIntervalRef.current = setInterval(() => {
                    if (cleanedUp) return;

                    const bufferCount = pcmBufferRef.current.length;
                    log(`⏰ Send interval tick | Buffers: ${bufferCount} | Muted: ${isMutedRef.current} | Active: ${callActiveRef.current}`);

                    const shouldSend =
                        pcmBufferRef.current.length > 0 &&
                        !isMutedRef.current &&
                        callActiveRef.current &&
                        sendMessageRef.current;

                    if (shouldSend) {
                        log('✅ Conditions met, sending audio...');
                        sendPCMAudioChunk();
                    } else {
                        // Log why not sending
                        if (bufferCount > 0) {
                            log('⏸️  Not sending audio:', {
                                hasBuffers: bufferCount,
                                muted: isMutedRef.current,
                                callActive: callActiveRef.current,
                                hasSendMessage: !!sendMessageRef.current
                            });
                        }

                        // Clear buffer if not sending to prevent buildup
                        if (bufferCount > 0) {
                            log(`🗑️  Clearing ${bufferCount} unused buffers`);
                        }
                        pcmBufferRef.current = [];
                        bufferCountRef.current = 0;
                    }
                }, STEP_IN_SEC * 1000);

                log(`✅ AudioWorklet recording ready | Send interval: ${STEP_IN_SEC}s`);

            } catch (error) {
                logError('❌ Error setting up AudioWorklet:', error);
                logError('Stack trace:', error.stack);
                setRecordingState('error');
                setAudioStats(prev => ({...prev, errors: prev.errors + 1}));
            }
        };

        setupAudioWorklet();

        return () => {
            cleanedUp = true;
            log('Cleaning up AudioWorklet recording...');

            if (sendIntervalRef.current) {
                clearInterval(sendIntervalRef.current);
                sendIntervalRef.current = null;
            }

            if (voiceDetectionIntervalRef.current) {
                clearInterval(voiceDetectionIntervalRef.current);
                voiceDetectionIntervalRef.current = null;
            }

            if (workletNodeRef.current) {
                try {
                    workletNodeRef.current.disconnect();
                } catch (error) {
                    logError('Error disconnecting worklet:', error);
                }
                workletNodeRef.current = null;
            }

            if (audioContextRef.current) {
                try {
                    audioContextRef.current.close();
                } catch (error) {
                    logError('Error closing AudioContext:', error);
                }
                audioContextRef.current = null;
            }

            analyserRef.current = null;
            pcmBufferRef.current = [];
            bufferCountRef.current = 0;

            log('✅ Cleanup complete');
            setRecordingState('idle');
            setIsSpeaking(false);
        };
    }, [localStream, isMuted, callActive]);

    // Send PCM audio chunk
    const sendPCMAudioChunk = () => {
        try {
            log(`📤 sendPCMAudioChunk called | Buffers in queue: ${pcmBufferRef.current.length}`);

            if (pcmBufferRef.current.length === 0) {
                log('⚠️  No PCM buffers to send');
                return;
            }

            // Calculate total length
            const totalLength = pcmBufferRef.current.reduce((sum, arr) => sum + arr.length, 0);
            log(`📊 Combining ${pcmBufferRef.current.length} buffers | Total samples: ${totalLength}`);

            // Combine all PCM chunks into single Int16Array
            const combinedPCM = new Int16Array(totalLength);
            let offset = 0;
            for (const chunk of pcmBufferRef.current) {
                combinedPCM.set(chunk, offset);
                offset += chunk.length;
            }

            // Clear buffer
            const buffersProcessed = pcmBufferRef.current.length;
            pcmBufferRef.current = [];
            bufferCountRef.current = 0;

            console.log(`🎤 Captured ${combinedPCM.length * BYTES_PER_SAMPLE} bytes PCM (~${STEP_IN_SEC}s)`);

            let audioBase64;
            if (USE_ULAW) {
                const muLaw = ulawFromPCM(combinedPCM);
                console.log(`🎤 Converted to ${muLaw.length} bytes μ-law`);

                // Check for audio content
                const hasAudio = muLaw.some((byte) => byte !== 255 && byte !== 127); // μ-law zero is 255 or 127
                if (!hasAudio) {
                    console.warn('🎤 Silent μ-law audio; skipping send.');
                    return;
                }
                // Convert μ-law Uint8Array to base64
                audioBase64 = arrayBufferToBase64(muLaw.buffer);
            } else {
                // Convert Int16Array to bytes
                const bytes = new Uint8Array(combinedPCM.buffer);
                const chunkSize = bytes.length;

                // Skip if too small
                if (chunkSize < 100) {
                    log('⏭️  Skipping small chunk:', chunkSize);
                    return;
                }
                // Convert to base64
                audioBase64 = btoa(String.fromCharCode(...bytes));
            }


            // Validate metadata
            const currentMetadata = metadataRef.current;
            if (!currentMetadata || !currentMetadata.room_code || !currentMetadata.user_id) {
                logError('❌ Invalid metadata:', currentMetadata);
                setAudioStats(prev => ({...prev, errors: prev.errors + 1}));
                return;
            }

            log('📡 Sending to backend via WebSocket...', {
                type: MessageType.AUDIO,
                room: currentMetadata.room_code,
                user: currentMetadata.user_id,
                audioLength: audioBase64.length
            });

            // Send to backend
            sendMessageRef.current({
                type: MessageType.AUDIO,
                msg_type: MessageType.AUDIO,
                audio_data: audioBase64,
                metadata: currentMetadata,
            });

            log('✅ Message sent to backend');

            // Update stats
            setAudioStats(prev => {
                const newTotal = prev.totalChunksSent + 1;
                const newTotalBytes = prev.totalBytesSent + buffersProcessed;
                const newStats = {
                    totalChunksSent: newTotal,
                    totalBytesSent: newTotalBytes,
                    lastChunkSize: buffersProcessed,
                    averageChunkSize: newTotalBytes / newTotal,
                    errors: prev.errors,
                };
                log(`✅ Stats updated | Total chunks: ${newTotal} | Total: ${(newTotalBytes / 1024).toFixed(2)} KB`);
                return newStats;
            });

            lastSendTimeRef.current = Date.now();

        } catch (error) {
            logError('❌ Error sending PCM audio:', error);
            logError('Stack trace:', error.stack);
            setAudioStats(prev => ({...prev, errors: prev.errors + 1}));
        }
    };

    // Voice Activity Detection
    useEffect(() => {
        if (!analyserRef.current || !localStream || isMuted || !callActive) {
            if (isSpeakingRef.current) {
                setIsSpeaking(false);
            }
            return;
        }

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        let consecutiveSilence = 0;
        let consecutiveSpeech = 0;
        const SILENCE_THRESHOLD = 3;
        const SPEECH_THRESHOLD = 2;
        const VOLUME_THRESHOLD = 3;

        log('🎤 Voice detection started | Threshold:', VOLUME_THRESHOLD);

        const checkAudioLevel = () => {
            if (!analyserRef.current || isMutedRef.current || !callActiveRef.current) {
                if (isSpeakingRef.current) {
                    setIsSpeaking(false);
                }
                return;
            }

            try {
                analyserRef.current.getByteFrequencyData(dataArray);
                const average = dataArray.reduce((a, b) => a + b) / dataArray.length;

                const isCurrentlySpeaking = average > VOLUME_THRESHOLD;

                if (isCurrentlySpeaking) {
                    consecutiveSpeech++;
                    consecutiveSilence = 0;

                    if (!isSpeakingRef.current && consecutiveSpeech >= SPEECH_THRESHOLD) {
                        setIsSpeaking(true);
                        log('🎤 Speaking started', {volume: average.toFixed(2)});
                    }
                } else {
                    consecutiveSilence++;
                    consecutiveSpeech = 0;

                    if (isSpeakingRef.current && consecutiveSilence >= SILENCE_THRESHOLD) {
                        setIsSpeaking(false);
                        log('🔇 Speaking stopped');
                    }
                }
            } catch (error) {
                logError('Error in voice detection:', error);
            }
        };

        voiceDetectionIntervalRef.current = setInterval(checkAudioLevel, 100);

        return () => {
            if (voiceDetectionIntervalRef.current) {
                clearInterval(voiceDetectionIntervalRef.current);
                voiceDetectionIntervalRef.current = null;
            }
            log('🎤 Voice detection stopped');
        };
    }, [localStream, isMuted, callActive]);

    return {
        isSpeaking,
        audioStats,
        recordingState,
    };
};